import { useRef } from 'react';

import { useCurrentSite } from '@hotjar/site';
import { DropdownMenu, IconButton } from '@hotjar/hotsauce';
import { ZapierFeature } from '@hotjar/integrations';
import { useTranslation } from '@hotjar/translations';
import { showDeleteSegmentModal } from '@hotjar/webapp/features/Segments/SegmentEditCreate/SegmentDeleteModal';

import type { Segment } from '@hotjar/webapp/features/Recordings/types';
import type { ButtonKind } from '@hotjar/hotsauce/build/components/IconButton/IconButton';

import { ForwardToSlackStatusBadge } from './ForwardToSlackStatusBadge';

export interface SegmentDropdownMenuProps {
  kind: ButtonKind | undefined;
  segment?: Segment;
  onEditSegment?: (segment: Segment) => void;
  onDeleteSegment?: (segment: Segment) => void;
  onEditSlackForwarding?: (segment: Segment) => void;
  trigger?: JSX.Element;
}

export function SegmentDropdownMenu({
  onDeleteSegment,
  onEditSegment,
  onEditSlackForwarding,
  segment,
  kind = 'secondary',
  trigger,
}: SegmentDropdownMenuProps): JSX.Element | null {
  const { t } = useTranslation('common');
  const currentSite = useCurrentSite().data;

  const forwardSlackRef = useRef<{ reload: () => void }>(null);

  if (!currentSite || !segment) {
    return null;
  }

  return (
    <DropdownMenu
      onShow={() => {
        forwardSlackRef.current?.reload();
      }}
      trigger={
        trigger ? (
          trigger
        ) : (
          <IconButton kind={kind} icon="menu_kebab" tooltipText="Show dropdown content" />
        )
      }
    >
      <DropdownMenu.Item
        isHidden={!onEditSegment}
        icon="pencil"
        onClick={() => onEditSegment?.(segment)}
      >
        {t('recordings.segment-edit-create.rename-segment')}
      </DropdownMenu.Item>

      <DropdownMenu.Item
        isHidden={!onDeleteSegment}
        icon="trash"
        onClick={() => {
          showDeleteSegmentModal(
            segment.name,
            () => {
              onDeleteSegment?.(segment);
            },
            () => {
              //
            },
          );
        }}
      >
        {t('recordings.segment-edit-create.delete-segment.dropdown-action')}
      </DropdownMenu.Item>

      <DropdownMenu.Item
        isDisabled
        onClick={() => {
          //
        }}
      >
        {t('recordings.segment-edit-create.forward-to')}
      </DropdownMenu.Item>

      <DropdownMenu.BaseItem
        isHidden={!onEditSlackForwarding}
        title="Slack"
        onClick={() => onEditSlackForwarding?.(segment)}
      >
        <ForwardToSlackStatusBadge
          ref={forwardSlackRef}
          siteId={currentSite?.id}
          segmentId={segment.id}
        ></ForwardToSlackStatusBadge>
      </DropdownMenu.BaseItem>

      <DropdownMenu.Item
        icon="zapier_small"
        onClick={() => {
          window.open(ZapierFeature.CREATE_ZAP_URL, '_blank');
        }}
      >
        Zapier
      </DropdownMenu.Item>
    </DropdownMenu>
  );
}
